/**
 * Publisher-account screens share one admin Business Unit query so selectors and read-only names
 * stay consistent without issuing per-account requests.
 */
import { useMemo } from 'react';
import { usePlatformAdminControllerFindBusinessUnitsQuery } from 'src/store/leela-api';
import { PERMISSION_LIST_PAGE_SIZE } from 'src/store/constants';
import type { BusinessUnitDto } from 'src/store/generated/leela-api-generated';

export const BUSINESS_UNIT_EMPTY_VALUE = '—';

interface BusinessUnitDisplayNameOptions {
  readonly businessUnitId?: string | null;
  readonly businessUnitNameMap: ReadonlyMap<string, string>;
  readonly isError: boolean;
  readonly unavailableLabel: string;
}

export const getBusinessUnitDisplayName = ({
  businessUnitId,
  businessUnitNameMap,
  isError,
  unavailableLabel,
}: BusinessUnitDisplayNameOptions): string => {
  if (!businessUnitId) {
    return BUSINESS_UNIT_EMPTY_VALUE;
  }
  if (isError) {
    return unavailableLabel;
  }
  return businessUnitNameMap.get(businessUnitId) ?? BUSINESS_UNIT_EMPTY_VALUE;
};

export const useBusinessUnitNameMap = () => {
  const { data, isLoading, isError } = usePlatformAdminControllerFindBusinessUnitsQuery({
    page: 1,
    pageSize: PERMISSION_LIST_PAGE_SIZE,
  });
  const businessUnits = useMemo(
    () => (data as { data?: BusinessUnitDto[] } | undefined)?.data ?? [],
    [data],
  );
  const businessUnitOptions = useMemo(
    () => businessUnits.map(businessUnit => ({
      label: businessUnit.name,
      value: businessUnit.id,
    })),
    [businessUnits],
  );
  const businessUnitNameMap = useMemo(
    () => new Map(businessUnits.map(businessUnit => [businessUnit.id, businessUnit.name])),
    [businessUnits],
  );

  return { businessUnitNameMap, businessUnitOptions, isLoading, isError };
};
